import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Box, Divider, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import colors from 'common/constants/colors';
import {
  LINK_CHECK_STATUS,
  describeDefaultNdcEffect,
  listStrengthFormPack,
  productNameOf,
} from '../utils/ndcProductLink';
import CatalogProductCard from './CatalogProductCard';
import LinkedNdcRows from './LinkedNdcRows';
import ProductFactGrid from './ProductFactGrid';
import SimilarProductsPanel from './SimilarProductsPanel';

// Open by default: comparing the new NDC with the product's existing ones is the point of the
// review, so they are not hidden behind a click. Longer lists show this many and "Show all".
const VISIBLE_LINKED_NDC_LIMIT = 3;

const PickedProductNdcs = ({ ndcs, replacedDefaultNdc, newDefaultNdc }) => (
  <Box data-testid='picked-product-ndcs'>
    <Divider sx={{ my: 1.5 }} />
    <Typography
      variant='caption'
      sx={{
        display: 'block',
        fontWeight: 700,
        letterSpacing: '0.08em',
        color: 'primary.main',
        mb: 1,
      }}
    >
      NDCS ALREADY LINKED ({ndcs.length})
    </Typography>
    {ndcs.length === 0 ? (
      <Typography variant='body2' color='text.secondary'>
        No NDCs are linked to this product yet. This NDC becomes the first one and its default.
      </Typography>
    ) : (
      <LinkedNdcRows
        ndcs={ndcs}
        replacedDefaultNdc={replacedDefaultNdc}
        newDefaultNdc={newDefaultNdc}
        visibleRowLimit={VISIBLE_LINKED_NDC_LIMIT}
      />
    )}
  </Box>
);

PickedProductNdcs.propTypes = {
  ndcs: PropTypes.arrayOf(PropTypes.shape({ ndc: PropTypes.string.isRequired })).isRequired,
  replacedDefaultNdc: PropTypes.string,
  newDefaultNdc: PropTypes.string,
};
PickedProductNdcs.defaultProps = {
  replacedDefaultNdc: undefined,
  newDefaultNdc: undefined,
};

// POM-6600 — step 2 of Add Inventory Item: the catalog product on top, the NDC inventory item it
// will be linked to below it (the ticket's product -> NDC order), similar products on the right.
// Nothing is created until Confirm association.
const ConfirmNdcProductLinkStep = ({
  selectedCatalog,
  enteredNdc,
  enteredDrugName,
  enteredStrength,
  enteredManufacturer,
  isSetAsDefault,
  currentDefaultNdc,
  linkCheck,
  linkCheckStatus,
  onRetry,
}) => {
  const defaultNdcEffect = describeDefaultNdcEffect({ currentDefaultNdc, isSetAsDefault });
  const pickedProductNdcs =
    linkCheckStatus === LINK_CHECK_STATUS.LOADED ? linkCheck?.selectedProduct?.ndcs : undefined;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        gap: 3,
        alignItems: 'start',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <CatalogProductCard
          label='CATALOG PRODUCT'
          productName={productNameOf(selectedCatalog)}
          productId={selectedCatalog.productId}
          testId='confirm-catalog-product'
          columnCount={3}
          facts={listStrengthFormPack(selectedCatalog)}
        >
          {pickedProductNdcs && (
            <PickedProductNdcs
              ndcs={pickedProductNdcs}
              replacedDefaultNdc={defaultNdcEffect.isReplacing ? currentDefaultNdc : undefined}
              newDefaultNdc={enteredNdc}
            />
          )}
        </CatalogProductCard>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <ArrowDownwardIcon fontSize='small' />
          <Typography variant='body2'>will be linked to this NDC inventory item</Typography>
        </Box>

        <Box
          data-testid='confirm-ndc-inventory-item'
          sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: colors.cardBorderGrey }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Typography
              variant='caption'
              sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}
            >
              NDC INVENTORY ITEM
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              What you entered on the form
            </Typography>
          </Box>
          <Typography variant='caption' color='text.secondary'>
            NDC
          </Typography>
          <Typography variant='h5' sx={{ fontWeight: 700, mb: 1.5 }}>
            {enteredNdc}
          </Typography>
          <ProductFactGrid
            facts={[
              { label: 'Drug name', value: enteredDrugName },
              { label: 'Strength', value: enteredStrength },
              { label: 'Manufacturer', value: enteredManufacturer },
              {
                label: 'Default NDC',
                value: defaultNdcEffect.answer,
                note: defaultNdcEffect.effect,
                noteSx: defaultNdcEffect.isReplacing
                  ? { color: colors.warningAmberText, fontWeight: 700 }
                  : undefined,
              },
            ]}
          />
        </Box>
      </Box>

      <SimilarProductsPanel
        linkCheck={linkCheck}
        linkCheckStatus={linkCheckStatus}
        onRetry={onRetry}
        pickedDrugName={selectedCatalog.drugName ?? ''}
      />
    </Box>
  );
};

ConfirmNdcProductLinkStep.propTypes = {
  selectedCatalog: PropTypes.shape({
    productId: PropTypes.string,
    displayName: PropTypes.string,
    drugName: PropTypes.string,
    strength: PropTypes.string,
    dosageForm: PropTypes.string,
    formAlias: PropTypes.string,
    packSizeValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    packSizeUnit: PropTypes.string,
  }).isRequired,
  enteredNdc: PropTypes.string.isRequired,
  enteredDrugName: PropTypes.string.isRequired,
  enteredStrength: PropTypes.string.isRequired,
  enteredManufacturer: PropTypes.string.isRequired,
  isSetAsDefault: PropTypes.bool.isRequired,
  currentDefaultNdc: PropTypes.string,
  linkCheck: PropTypes.shape({
    selectedProduct: PropTypes.shape({ ndcs: PropTypes.arrayOf(PropTypes.shape({})) }),
  }),
  linkCheckStatus: PropTypes.oneOf(Object.values(LINK_CHECK_STATUS)).isRequired,
  onRetry: PropTypes.func.isRequired,
};
ConfirmNdcProductLinkStep.defaultProps = {
  currentDefaultNdc: undefined,
  linkCheck: null,
};

export default ConfirmNdcProductLinkStep;
